import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import useImageUploader from "../../../../components/ui/ImageUploader";
import { Form, FormField, Input, ModalDialog, Numeric } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import { SecondaryItem, UpdateSecondaryItemBody } from "../../../../http/deckDesigner/types/secondaryItems";
import useDeckSecondaryItemsApi from "../../../../http/deckDesigner/useSecondaryItems";

interface SecondaryItemFormBody {
  thickness: number | null;
  length: number | null;
}

interface SecondaryItemFormProps {
  secondaryItem: SecondaryItem;
  onClose: () => void;
}

const positiveMinValue = Number.MIN_VALUE;

const SecondaryItemForm: FC<SecondaryItemFormProps> = ({ secondaryItem, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const secondaryItemsApi = useDeckSecondaryItemsApi();

  const defaultValues: SecondaryItemFormBody = {
    thickness: secondaryItem.thickness,
    length: secondaryItem.length,
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: secondaryItem.image ?? undefined,
    isLoading: false,
  });

  const onSubmit = () => async (formData: SecondaryItemFormBody) => {
    if (formData.thickness == null || formData.length == null) {
      return;
    }

    const body: UpdateSecondaryItemBody = {
      itemId: secondaryItem.item.id,
      thickness: formData.thickness,
      length: formData.length,
    };

    try {
      const image = await secondaryItemsApi.uploadImage(secondaryItem.item.id, newImage, removeImage);
      await secondaryItemsApi.updateDeckSecondaryItem(secondaryItem.item.id, { ...body, ...image });
      onSave();
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.secondaryItems.singular}`}
    >
      <Form<SecondaryItemFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth imageUploader={component}>
            <FormField label={i18n.translation.common.code}>
              {({ labelId }) => <Input id={labelId} value={secondaryItem.item.code} disabled />}
            </FormField>
            <FormField label={i18n.translation.common.description}>
              {({ labelId }) => <Input id={labelId} value={secondaryItem.item.description} disabled />}
            </FormField>
            <Controller
              name="thickness"
              control={control}
              rules={{ required: true, min: positiveMinValue }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.thickness} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={positiveMinValue}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="length"
              control={control}
              rules={{ required: true, min: positiveMinValue }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.length} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={positiveMinValue}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default SecondaryItemForm;
